import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Lightning, ArrowUpRight } from "@phosphor-icons/react";
import Hero3D from "./Hero3D";
import KoshMark from "./KoshMark";

const chips = [
  { label: "Computer Vision", delay: 0.4, pos: "top-[18%] left-[6%]", anim: "ww-drift-a" },
  { label: "LLM Copilots", delay: 0.6, pos: "top-[28%] right-[8%]", anim: "ww-drift-b" },
  { label: "Voice Bots", delay: 0.8, pos: "bottom-[26%] left-[10%]", anim: "ww-drift-b" },
  { label: "Live Dashboards", delay: 1.0, pos: "bottom-[18%] right-[12%]", anim: "ww-drift-a" },
  { label: "Optimization", delay: 1.2, pos: "top-[8%] right-[24%]", anim: "ww-drift-a" },
];

export default function Hero() {
  return (
    <section data-testid="hero-section" className="relative w-full min-h-[92vh] overflow-hidden border-b border-[#2A2A2A]">
      <Hero3D />
      <div className="absolute inset-0 ww-vignette pointer-events-none" />
      <div className="absolute inset-0 ww-grid-bg opacity-30 pointer-events-none" />

      {/* Floating chips */}
      {chips.map((c) => (
        <motion.div
          key={c.label}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: c.delay, duration: 0.6 }}
          className={`hidden md:block absolute ${c.pos} ${c.anim} pointer-events-none`}
        >
          <div className="ww-chip ww-glass">
            <span className="dot" />
            {c.label}
          </div>
        </motion.div>
      ))}

      {/* Top instrument bar */}
      <div className="relative z-10 px-6 md:px-14 pt-10 flex items-center justify-between font-mono text-[10px] tracking-[0.25em] uppercase text-[#8E8E93]">
        <span data-testid="hero-instrument-left">SYS / WISEWIT_CORE_v1</span>
        <span className="hidden md:inline">LAT 12.97°N / LON 77.59°E</span>
        <span>STATUS · <span className="text-[#DFFF00]">RUNNING</span></span>
      </div>

      <div className="relative z-10 px-6 md:px-14 pt-20 md:pt-28 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-overline mb-6"
        >
          ◢ AI systems for industrial execution
        </motion.div>

        <motion.h1
          data-testid="hero-headline"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="font-display font-bold tracking-tighter text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.95]"
        >
          We build the systems
          <br />
          that <span className="text-[#FF4500]">run</span> the floor.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.25 }}
          className="mt-6 max-w-2xl text-[#8E8E93] text-lg leading-relaxed"
        >
          Computer vision, LLM copilots, voice interfaces, command-center dashboards, automation and
          optimization — engineered into one execution layer for plants, lines, and operations.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link to="/contact" data-testid="hero-cta-primary" className="btn-primary">
            Start a project <ArrowRight size={14} weight="bold" />
          </Link>
          <Link to="/reels" data-testid="hero-cta-secondary" className="btn-secondary">
            <Lightning size={14} weight="fill" color="#DFFF00" /> See it in motion
          </Link>
        </motion.div>

        {/* KOSH intro card — makes the copilot discoverable to first-time visitors */}
        <motion.button
          data-testid="hero-kosh-card"
          onClick={() => window.dispatchEvent(new CustomEvent("kosh:open"))}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.55 }}
          className="mt-6 group inline-flex items-center gap-4 pl-3 pr-5 py-3 border border-[#FF4500]/40 bg-[#FF4500]/[0.04] hover:bg-[#FF4500]/[0.08] hover:border-[#FF4500] transition-all text-left"
          style={{ boxShadow: "0 0 0 1px rgba(255,69,0,0.08), 0 8px 32px rgba(255,69,0,0.06)" }}
        >
          <span
            className="flex items-center justify-center w-10 h-10 shrink-0"
            style={{ background: "#FF4500", borderRadius: "9999px" }}
          >
            <KoshMark size={22} tone="on-color" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#FF4500] flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF4500] ww-pulse" />
              Meet KOSH
            </div>
            <div className="mt-0.5 text-[13px] text-[#F5F5F5]">
              Wisewit's AI copilot. Ask anything about what we build.
            </div>
          </div>
          <ArrowUpRight
            size={16}
            weight="bold"
            className="text-[#FF4500] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
          />
        </motion.button>

        {/* numeric strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="mt-20 md:mt-28 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2A2A2A] border border-[#2A2A2A]"
        >
          {[
            { k: "Inspection cycle", v: "8.5x", sub: "faster" },
            { k: "Response time", v: "-94%", sub: "to plant queries" },
            { k: "Reporting", v: "Live", sub: "cross-site" },
            { k: "Unplanned events", v: "-31%", sub: "quarter on quarter" },
          ].map((s, i) => (
            <div key={i} className="bg-[#050505] px-6 py-6">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8E8E93]">{s.k}</div>
              <div className="font-display font-bold text-3xl text-white mt-2">{s.v}</div>
              <div className="font-mono text-[11px] text-[#555] mt-1">{s.sub}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
